import React, { useState } from 'react';
import styled from 'styled-components';
import {
  PaymentPlanDto,
  PaymentInstallmentInfo,
  PaymentMethod,
  getPaymentMethodName,
  formatCurrency,
  formatDate,
  paymentPlansApi,
  handleApiError
} from '../../services/paymentsService';

interface ConfirmInstallmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  paymentPlan: PaymentPlanDto;
  installment: PaymentInstallmentInfo;
  onSuccess: () => void;
}

const Overlay = styled.div<{ $isOpen: boolean }>`
  display: ${props => props.$isOpen ? 'flex' : 'none'};
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  justify-content: center;
  align-items: center;
  z-index: 1000;
  padding: 20px;
`;

const Modal = styled.div`
  background: white;
  border-radius: var(--border-radius-lg);
  padding: 32px;
  max-width: 500px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: var(--shadow-dark);

  @media (max-width: 767px) {
    padding: 20px;
    max-width: 100%;
  }
`;

const Title = styled.h2`
  margin: 0 0 24px 0;
  font-size: 20px;
  font-weight: 600;
  color: var(--text-primary);
  text-align: right;
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 8px;
  font-size: 14px;
  font-weight: 600;
  color: var(--text-primary);
  text-align: right;
`;

const Input = styled.input`
  width: 100%;
  padding: 10px 12px;
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-md);
  font-size: 14px;
  font-family: inherit;
  transition: all 0.2s ease;
  direction: rtl;

  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 3px rgba(0, 142, 162, 0.1);
  }

  &:disabled {
    background: var(--background-secondary);
    cursor: not-allowed;
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 10px 12px;
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-md);
  font-size: 14px;
  font-family: inherit;
  transition: all 0.2s ease;
  direction: rtl;
  background: white;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 3px rgba(0, 142, 162, 0.1);
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 10px 12px;
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-md);
  font-size: 14px;
  font-family: inherit;
  transition: all 0.2s ease;
  direction: rtl;
  resize: vertical;
  min-height: 80px;

  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 3px rgba(0, 142, 162, 0.1);
  }
`;

const InfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 10px 0;
  border-bottom: 1px solid var(--border-color);
  margin-bottom: 16px;

  &:last-of-type {
    margin-bottom: 24px;
  }
`;

const InfoLabel = styled.span`
  font-size: 14px;
  color: var(--text-secondary);
`;

const InfoValue = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: var(--text-primary);
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 24px;

  @media (max-width: 767px) {
    flex-direction: column-reverse;
  }
`;

const Button = styled.button<{ variant?: 'primary' | 'secondary' }>`
  padding: 10px 24px;
  border-radius: var(--border-radius-md);
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;

  ${props => props.variant === 'primary' ? `
    background: var(--primary-color);
    color: white;

    &:hover:not(:disabled) {
      background: var(--primary-light); color: var(--primary-color);
      transform: translateY(-1px);
      box-shadow: var(--shadow-light);
    }
  ` : `
    background: white;
    color: var(--text-primary);
    border: 1px solid var(--border-color);

    &:hover:not(:disabled) {
      background: var(--background-secondary);
    }
  `}

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  @media (max-width: 767px) {
    width: 100%;
  }
`;

const ErrorMessage = styled.div`
  background: rgba(220, 38, 38, 0.1);
  border: 1px solid rgba(220, 38, 38, 0.3);
  border-radius: var(--border-radius-md);
  padding: 12px;
  margin-bottom: 16px;
  color: #dc2626;
  font-size: 14px;
  text-align: right;
`;

const FieldError = styled.div`
  color: #dc2626;
  font-size: 12px;
  margin-top: 4px;
  font-weight: 500;
`;

const FieldWarning = styled.div`
  color: #b45309;
  font-size: 12px;
  margin-top: 4px;
`;

const SuccessMessage = styled.div`
  background: rgba(22, 163, 74, 0.1);
  border: 1px solid rgba(22, 163, 74, 0.3);
  border-radius: var(--border-radius-md);
  padding: 12px;
  margin-bottom: 16px;
  color: #16a34a;
  font-size: 14px;
  text-align: right;
`;

const ConfirmInstallmentModal: React.FC<ConfirmInstallmentModalProps> = ({
  isOpen,
  onClose,
  paymentPlan,
  installment,
  onSuccess
}) => {
  const [paymentDate, setPaymentDate] = useState(new Date().toISOString().split('T')[0]);
  const [amount, setAmount] = useState(installment.amount.toString());
  const [paymentMethod, setPaymentMethod] = useState<number>(PaymentMethod.BankTransfer);
  const [referenceNumber, setReferenceNumber] = useState('');
  const [notes, setNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [amountError, setAmountError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const amountNumber = parseFloat(amount);
  // סכום 0 סימן בעבר את התשלום "שולם" בלי כסף — ונסגרה תוכנית עם חוב
  const validateAmount = (): string | null =>
    isNaN(amountNumber) || amountNumber <= 0 ? 'יש להזין סכום גדול מאפס' : null;
  // סכום נמוך מהמתוכנן מותר (תשלום חלקי), אבל ההפרש נשאר כיתרה — שהמשתמש יידע לפני השמירה
  const isBelowPlanned = !isNaN(amountNumber) && amountNumber > 0
    && amountNumber < installment.amount - 0.005;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    const validationError = validateAmount();
    setAmountError(validationError);
    if (validationError) return;
    setIsSubmitting(true);

    try {
      await paymentPlansApi.confirmInstallment({
        paymentPlanId: paymentPlan.id,
        installmentNumber: installment.installmentNumber,
        paymentDate,
        amount: parseFloat(amount),
        paymentMethod,
        referenceNumber: referenceNumber.trim() || undefined,
        notes: notes.trim() || undefined
      });

      setSuccess(true);
      setTimeout(() => {
        onSuccess();
      }, 1000);
    } catch (err: any) {
      setError(handleApiError(err));
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <Overlay $isOpen={isOpen}>
      <Modal>
        <Title>אישור קבלת תשלום - תשלום {installment.installmentNumber}</Title>

        <InfoRow>
          <InfoLabel>תאריך מועד</InfoLabel>
          <InfoValue>{formatDate(installment.dueDate)}</InfoValue>
        </InfoRow>

        <InfoRow>
          <InfoLabel>סכום מתוכנן</InfoLabel>
          <InfoValue>{formatCurrency(installment.amount)}</InfoValue>
        </InfoRow>

        {error && <ErrorMessage>{error}</ErrorMessage>}
        {success && <SuccessMessage>התשלום נרשם בהצלחה!</SuccessMessage>}

        <form onSubmit={handleSubmit}>
          <FormGroup>
            <Label>תאריך קבלת תשלום *</Label>
            <Input
              type="date"
              value={paymentDate}
              onChange={(e) => setPaymentDate(e.target.value)}
              required
              disabled={isSubmitting || success}
            />
          </FormGroup>

          <FormGroup>
            <Label>סכום שהתקבל *</Label>
            <Input
              type="number"
              step="0.01"
              value={amount}
              onChange={(e) => {
                setAmount(e.target.value);
                setAmountError(null);
              }}
              onBlur={() => setAmountError(validateAmount())}
              required
              disabled={isSubmitting || success}
              min="0.01"
              style={amountError ? { borderColor: '#dc2626' } : undefined}
            />
            {amountError ? (
              <FieldError>{amountError}</FieldError>
            ) : isBelowPlanned && (
              <FieldWarning>
                הסכום נמוך מסכום התשלום ({formatCurrency(installment.amount)}) — ההפרש יישאר כיתרה לתשלום
              </FieldWarning>
            )}
          </FormGroup>

          <FormGroup>
            <Label>אמצעי תשלום *</Label>
            <Select
              value={paymentMethod}
              onChange={(e) => setPaymentMethod(Number(e.target.value))}
              required
              disabled={isSubmitting || success}
            >
              <option value={PaymentMethod.BankTransfer}>{getPaymentMethodName(PaymentMethod.BankTransfer)}</option>
              <option value={PaymentMethod.Cash}>{getPaymentMethodName(PaymentMethod.Cash)}</option>
              <option value={PaymentMethod.Check}>{getPaymentMethodName(PaymentMethod.Check)}</option>
              <option value={PaymentMethod.CreditCard}>{getPaymentMethodName(PaymentMethod.CreditCard)}</option>
              <option value={PaymentMethod.DirectDebit}>{getPaymentMethodName(PaymentMethod.DirectDebit)}</option>
              <option value={PaymentMethod.Other}>{getPaymentMethodName(PaymentMethod.Other)}</option>
            </Select>
          </FormGroup>

          <FormGroup>
            <Label>מספר אסמכתא</Label>
            <Input
              type="text"
              value={referenceNumber}
              onChange={(e) => setReferenceNumber(e.target.value)}
              placeholder="מספר העברה, צ'ק וכו'"
              disabled={isSubmitting || success}
            />
          </FormGroup>

          <FormGroup>
            <Label>הערות</Label>
            <TextArea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="הערות נוספות..."
              disabled={isSubmitting || success}
            />
          </FormGroup>

          <ButtonGroup>
            <Button type="button" variant="secondary" onClick={onClose} disabled={isSubmitting}>
              ביטול
            </Button>
            <Button type="submit" variant="primary" disabled={isSubmitting || success}>
              {isSubmitting ? 'שומר...' : 'אשר קבלת תשלום'}
            </Button>
          </ButtonGroup>
        </form>
      </Modal>
    </Overlay>
  );
};

export default ConfirmInstallmentModal;
